// map of pathwright domains to environment
export const PATHWRIGHT_DOMAIN_RES = new Map([
  [/\.(pathwright)\.com/, "production"],
  [/\.(pathwrightstaging)\.com/, "staging"],
  [/\.(pathwrightdev)\.com/, "development"],
  [/\.(pantswright)\.com/, "testing"]
])

type Environment = "production" | "staging" | "development" | "testing"
type EnvironmentMatcher = Partial<Record<Environment | "_", any>>
type DomainMatch = { match: RegExpMatchArray; env: Environment } | null

// find the matching pathwright domain re entry, replacing the re with the match
export const getPathwrightDomainMatch = (url?: string): DomainMatch => {
  const hostname = url || window.location.hostname
  return [...PATHWRIGHT_DOMAIN_RES.entries()].reduce<DomainMatch>(
    (result, [re, env]) =>
      result
        ? result
        : re.test(hostname)
        ? { match: hostname.match(re)!, env: env as Environment }
        : null,
    null
  )
}

// get the environment that matches the window.location.hostname
export const getDomainEnv = (url?: string): Environment | undefined => {
  return getPathwrightDomainMatch(url)?.env
}

// get the pathwright domain from the window.location.hostname
export const getPathwrightDomain = (url?: string): string | undefined => {
  return getPathwrightDomainMatch(url)?.match[1]
}

export type TargetEnv = Environment | Environment[] | EnvironmentMatcher

export const getEnv = (targetEnv?: TargetEnv): Environment => {
  // get environment based on domain
  let env = getDomainEnv()

  switch (process.env.NODE_ENV) {
    // only allow production environment when set via NODE_ENV
    case "production":
      // and when not overridden by domain
      if (!env) env = "production"
      break
    case "test":
      // Ensure test env is not overridden.
      env = "test" as Environment
      break
  }

  // default to development environment
  if (!env) env = "development"

  if (targetEnv) {
    return getTargetEnv(env, targetEnv!)
  }

  return env
}

export function getTargetEnv(
  env: Environment,
  targetEnv: TargetEnv
): boolean | any {
  // targetEnv may include env as item in array
  if (Array.isArray(targetEnv)) return targetEnv.includes(env)
  // targetEnv may include env as key in object, or default to _ key
  if (typeof targetEnv === "object" && targetEnv !== null)
    return Object.prototype.hasOwnProperty.call(targetEnv, env)
      ? (targetEnv as EnvironmentMatcher)[env]
      : (targetEnv as EnvironmentMatcher)._
  // targetEnv may match env as string
  if (typeof targetEnv === "string") return env === targetEnv

  return env
}

// Get the env in which the application is currently running, based on the domain.
// This is primarily useful for determining the correct target env when the app
// is running in a local dev environment, where the domain will not indicate the
// intended target env.
export function getOperatingEnv() {
  // "school_config_url" should only exist in local dev.
  const hostname = getIsLocalHost()
    ? localStorage.getItem("school_config_url")
    : window.location.hostname

  let env = getDomainEnv(hostname!)

  // If we can't determine env from hostname, we must be in production...
  if (!env && hostname) {
    // ... either the deployed production env with a custom domain
    if (process.env.NODE_ENV === "production") {
      env = "production"
    }

    // ... or a local dev env using a production API
    if (getIsLocalHost()) {
      env = "production"
    }
  }

  if (!env) {
    throw new Error("Could not determine admin environment.")
  }

  return env
}

export const getIsLocalHost = (): boolean =>
  !getDomainEnv() && getEnv() !== "production"
export const getIsLocalDev = (): boolean =>
  getIsLocalHost() || getEnv() === "development"
